import React, { useState, useEffect, useMemo } from 'react';
import { Paper, Button, Alert, FormControlLabel, Checkbox, Typography, Box, FormControl, InputLabel, Select, MenuItem, TextField, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SitesService from '../../../services/SitesService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { useSitesConfig } from '../SitesContext';
import { prepareFieldUpdates } from '../utils/fieldUtils';
import { getLocations } from '../../../services/LocationService';

const PAYMENT_CONFIG_DEFAULTS = {
  mercadopago: {
    title: 'Mercado Pago',
    description: 'Pagá online al instante con Mercado Pago.'
  },
  transfer: {
    title: 'Transferencia bancaria',
    description: 'Realizá la transferencia y mandanos un mensaje con el comprobante para confirmar tu pedido.',
    bankName: '',
    accountHolder: '',
    accountNumber: '',
    accountType: '',
    branch: '',
    instructions: 'Después de transferir, mandanos un mensaje con el comprobante para validar el pago.'
  },
  cash: {
    title: 'Efectivo',
    description: 'Pagá al retirar o al recibir tu pedido.',
    instructions: ''
  },
  pos: {
    title: 'POS',
    description: 'Pagá con POS al retirar o recibir tu pedido.',
    instructions: 'El pago queda pendiente y lo confirmamos manualmente cuando se complete en el POS.'
  }
};

const PAYMENT_FIELD_PATHS = {
  mercadopago: [
    'siteConfig.paymentMethods.mercadopago.title',
    'siteConfig.paymentMethods.mercadopago.description'
  ],
  transfer: [
    'siteConfig.paymentMethods.transfer.title',
    'siteConfig.paymentMethods.transfer.description',
    'siteConfig.paymentMethods.transfer.bankName',
    'siteConfig.paymentMethods.transfer.accountHolder',
    'siteConfig.paymentMethods.transfer.accountNumber',
    'siteConfig.paymentMethods.transfer.accountType',
    'siteConfig.paymentMethods.transfer.branch',
    'siteConfig.paymentMethods.transfer.instructions'
  ],
  cash: [
    'siteConfig.paymentMethods.cash.title',
    'siteConfig.paymentMethods.cash.description',
    'siteConfig.paymentMethods.cash.instructions'
  ],
  pos: [
    'siteConfig.paymentMethods.pos.title',
    'siteConfig.paymentMethods.pos.description',
    'siteConfig.paymentMethods.pos.instructions'
  ]
};

const valueOrDefault = (value, defaultValue) => {
  if (typeof value === 'string' && value.trim().length > 0) return value;
  return defaultValue;
};

const EcommerceTab = ({ leadingContent }) => {
  const { t } = useTranslation();
  const { config, isFieldConfigurable, loadConfig } = useSitesConfig();
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [ecommerceLocationIds, setEcommerceLocationIds] = useState([]);
  const [ecommerceDefaultLocationId, setEcommerceDefaultLocationId] = useState(null);
  const [ecommerceMode, setEcommerceMode] = useState('one_page');
  const [enabledPaymentMethods, setEnabledPaymentMethods] = useState([]);
  const [paymentConfigs, setPaymentConfigs] = useState({
    mercadopago: { ...PAYMENT_CONFIG_DEFAULTS.mercadopago },
    transfer: { ...PAYMENT_CONFIG_DEFAULTS.transfer },
    cash: { ...PAYMENT_CONFIG_DEFAULTS.cash },
    pos: { ...PAYMENT_CONFIG_DEFAULTS.pos }
  });

  // Cargar sucursales disponibles en la web y sucursal por defecto
  useEffect(() => {
    const ids = config.siteConfig?.ecommerceLocationIds;
    setEcommerceLocationIds(Array.isArray(ids) ? ids.map(Number) : []);
    const defaultId = config.siteConfig?.ecommerceDefaultLocationId;
    setEcommerceDefaultLocationId(defaultId != null ? Number(defaultId) : null);
    setEcommerceMode(config.siteConfig?.ecommerceMode || 'one_page');
    const paymentMethods = config.siteConfig?.paymentMethods || {};
    setEnabledPaymentMethods(Array.isArray(paymentMethods.enabled) ? paymentMethods.enabled : ['mercadopago']);
    setPaymentConfigs({
      mercadopago: {
        title: valueOrDefault(paymentMethods.mercadopago?.title, PAYMENT_CONFIG_DEFAULTS.mercadopago.title),
        description: valueOrDefault(paymentMethods.mercadopago?.description, PAYMENT_CONFIG_DEFAULTS.mercadopago.description)
      },
      transfer: {
        title: valueOrDefault(paymentMethods.transfer?.title, PAYMENT_CONFIG_DEFAULTS.transfer.title),
        description: valueOrDefault(paymentMethods.transfer?.description, PAYMENT_CONFIG_DEFAULTS.transfer.description),
        bankName: paymentMethods.transfer?.bankName || '',
        accountHolder: paymentMethods.transfer?.accountHolder || '',
        accountNumber: paymentMethods.transfer?.accountNumber || '',
        accountType: paymentMethods.transfer?.accountType || '',
        branch: paymentMethods.transfer?.branch || '',
        instructions: valueOrDefault(paymentMethods.transfer?.instructions, PAYMENT_CONFIG_DEFAULTS.transfer.instructions)
      },
      cash: {
        title: valueOrDefault(paymentMethods.cash?.title, PAYMENT_CONFIG_DEFAULTS.cash.title),
        description: valueOrDefault(paymentMethods.cash?.description, PAYMENT_CONFIG_DEFAULTS.cash.description),
        instructions: paymentMethods.cash?.instructions || ''
      },
      pos: {
        title: valueOrDefault(paymentMethods.pos?.title, PAYMENT_CONFIG_DEFAULTS.pos.title),
        description: valueOrDefault(paymentMethods.pos?.description, PAYMENT_CONFIG_DEFAULTS.pos.description),
        instructions: valueOrDefault(paymentMethods.pos?.instructions, PAYMENT_CONFIG_DEFAULTS.pos.instructions)
      }
    });
  }, [config]);

  const availableLocations = useMemo(
    () => locations.filter((location) => !location.isPlant),
    [locations]
  );
  const availableLocationIds = useMemo(
    () => new Set(availableLocations.map((location) => Number(location.id))),
    [availableLocations]
  );

  useEffect(() => {
    if (loadingLocations) return;

    setEcommerceLocationIds((prev) => {
      const next = prev.filter((id) => availableLocationIds.has(Number(id)));
      setEcommerceDefaultLocationId((current) => (
        current != null && next.includes(Number(current)) ? Number(current) : next[0] ?? null
      ));
      return next.length === prev.length ? prev : next;
    });
  }, [availableLocationIds, loadingLocations]);


  // Cargar locations disponibles
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoadingLocations(true);
        const locationsData = await getLocations();
        setLocations(locationsData || []);
      } catch (error) {
        console.error('Error loading locations:', error);
        SnackbarUtils.error('Error al cargar las sucursales');
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchLocations();
  }, []);

  const handleLocationToggle = (locationId) => {
    setEcommerceLocationIds((prev) => {
      const id = Number(locationId);
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      if (!next.includes(ecommerceDefaultLocationId)) {
        setEcommerceDefaultLocationId(next.length ? next[0] : null);
      }
      return next;
    });
  };

  const handlePaymentMethodToggle = (method) => {
    setEnabledPaymentMethods((prev) => {
      const isEnabled = prev.includes(method);
      if (!isEnabled) {
        setPaymentConfigs((current) => ({
          ...current,
          [method]: {
            ...PAYMENT_CONFIG_DEFAULTS[method],
            ...current[method]
          }
        }));
      }
      return isEnabled ? prev.filter((item) => item !== method) : [...prev, method];
    });
  };

  const handlePaymentConfigChange = (method, field, value) => {
    setPaymentConfigs((prev) => ({
      ...prev,
      [method]: {
        ...prev[method],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const locationsPath = 'siteConfig.ecommerceLocationIds';
      const defaultLocationPath = 'siteConfig.ecommerceDefaultLocationId';
      const ecommerceModePath = 'siteConfig.ecommerceMode';
      const paymentMethodsEnabledPath = 'siteConfig.paymentMethods.enabled';

      if (isFieldConfigurable(locationsPath) && ecommerceLocationIds.length === 0) {
        SnackbarUtils.warning(t('ecommerce_locations_required') || 'Debe seleccionar al menos una sucursal disponible en la web');
        return;
      }
      if (enabledPaymentMethods.length === 0) {
        SnackbarUtils.warning(t('at_least_one_payment_method_required') || 'Debe haber al menos un método de pago habilitado');
        return;
      }

      const defaultId = ecommerceDefaultLocationId != null && ecommerceLocationIds.includes(ecommerceDefaultLocationId)
        ? ecommerceDefaultLocationId
        : ecommerceLocationIds[0];

      const updates = {
        [locationsPath]: ecommerceLocationIds,
        [defaultLocationPath]: defaultId,
        [paymentMethodsEnabledPath]: enabledPaymentMethods,
        'siteConfig.paymentMethods.mercadopago.title': paymentConfigs.mercadopago.title,
        'siteConfig.paymentMethods.mercadopago.description': paymentConfigs.mercadopago.description,
        'siteConfig.paymentMethods.transfer.title': paymentConfigs.transfer.title,
        'siteConfig.paymentMethods.transfer.description': paymentConfigs.transfer.description,
        'siteConfig.paymentMethods.transfer.bankName': paymentConfigs.transfer.bankName,
        'siteConfig.paymentMethods.transfer.accountHolder': paymentConfigs.transfer.accountHolder,
        'siteConfig.paymentMethods.transfer.accountNumber': paymentConfigs.transfer.accountNumber,
        'siteConfig.paymentMethods.transfer.accountType': paymentConfigs.transfer.accountType,
        'siteConfig.paymentMethods.transfer.branch': paymentConfigs.transfer.branch,
        'siteConfig.paymentMethods.transfer.instructions': paymentConfigs.transfer.instructions,
        'siteConfig.paymentMethods.cash.title': paymentConfigs.cash.title,
        'siteConfig.paymentMethods.cash.description': paymentConfigs.cash.description,
        'siteConfig.paymentMethods.cash.instructions': paymentConfigs.cash.instructions,
        'siteConfig.paymentMethods.pos.title': paymentConfigs.pos.title,
        'siteConfig.paymentMethods.pos.description': paymentConfigs.pos.description,
        'siteConfig.paymentMethods.pos.instructions': paymentConfigs.pos.instructions
      };

      Object.entries(PAYMENT_FIELD_PATHS).forEach(([method, fieldPaths]) => {
        if (!enabledPaymentMethods.includes(method)) {
          fieldPaths.forEach((fieldPath) => {
            delete updates[fieldPath];
          });
        }
      });

      if (isFieldConfigurable(ecommerceModePath)) {
        updates[ecommerceModePath] = ecommerceMode;
        updates['siteConfig.pages.menu.enabled'] = ecommerceMode !== 'store';
        updates['siteConfig.pages.tienda.enabled'] = ecommerceMode === 'store';
      }

      const configurableUpdates = Object.fromEntries(
        Object.entries(updates).filter(([fieldPath]) => isFieldConfigurable(fieldPath))
      );

      if (Object.keys(configurableUpdates).length === 0) {
        SnackbarUtils.warning(t('no_editable_fields_in_section') || 'No hay campos editables en esta sección para el template actual');
        return;
      }

      const preparedUpdates = prepareFieldUpdates(configurableUpdates);
      await SitesService.updateFields(preparedUpdates);

      // Recargar configuración para obtener valores actualizados
      await loadConfig();
      
      SnackbarUtils.success(t('section_saved_successfully') || 'Sección guardada exitosamente');
    } catch (err) {
      console.error('Error saving ecommerce config:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      SnackbarUtils.error(errorMessage || (t('error_saving_section') || 'Error al guardar la sección'));
    } finally {
      setSaving(false);
    }
  };

  const locationsPath = 'siteConfig.ecommerceLocationIds';
  const defaultLocationPath = 'siteConfig.ecommerceDefaultLocationId';
  const ecommerceModePath = 'siteConfig.ecommerceMode';
  const paymentMethodsEnabledPath = 'siteConfig.paymentMethods.enabled';
  const hasEditableFields =
    isFieldConfigurable(locationsPath) ||
    isFieldConfigurable(defaultLocationPath) ||
    isFieldConfigurable(ecommerceModePath) ||
    isFieldConfigurable(paymentMethodsEnabledPath) ||
    Object.values(PAYMENT_FIELD_PATHS).flat().some((path) => isFieldConfigurable(path));

  const isAnyPaymentFieldConfigurable = [
    paymentMethodsEnabledPath,
    ...Object.values(PAYMENT_FIELD_PATHS).flat()
  ].some((path) => isFieldConfigurable(path));

  const canConfigurePaymentMethod = (method) =>
    enabledPaymentMethods.includes(method) &&
    PAYMENT_FIELD_PATHS[method].some((path) => isFieldConfigurable(path));

  if (!hasEditableFields) {
    return (
      <Paper sx={{ padding: 3, mt: 2 }}>
        <Alert severity="info">
          {t('no_editable_fields_in_section') || 'No hay campos editables en esta sección para el template actual'}
        </Alert>
      </Paper>
    );
  }

  return (
    <>
      {/* Card Sucursales disponibles en la web */}
      {isFieldConfigurable(ecommerceModePath) && (
        <Paper sx={{ padding: 3, mt: 2 }}>
          {leadingContent}
          <Typography variant="h6" gutterBottom>
            {t('ecommerce_view_mode') || 'Vista del ecommerce'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('ecommerce_view_mode_description') || 'Elegí si querés vender dentro de la landing one page o en una tienda separada. Es una opción o la otra.'}
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="ecommerce-mode-label">
              {t('ecommerce_view_mode') || 'Vista del ecommerce'}
            </InputLabel>
            <Select
              labelId="ecommerce-mode-label"
              value={ecommerceMode}
              label={t('ecommerce_view_mode') || 'Vista del ecommerce'}
              onChange={(e) => setEcommerceMode(e.target.value)}
              disabled={saving}
            >
              <MenuItem value="one_page">{t('ecommerce_view_mode_one_page') || 'One page'}</MenuItem>
              <MenuItem value="store">{t('ecommerce_view_mode_store') || 'Con tienda'}</MenuItem>
            </Select>
          </FormControl>
        </Paper>
      )}

      {isFieldConfigurable(locationsPath) && (
        <Paper sx={{ padding: 3, mt: 2 }}>
          {!isFieldConfigurable(ecommerceModePath) && leadingContent}
          <Typography variant="h6" gutterBottom>
            {t('ecommerce_locations_web') || 'Sucursales disponibles en la web'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('ecommerce_locations_web_description') || 'Selecciona las sucursales que aparecerán en la tienda. El usuario elegirá una al entrar; stock y precios se muestran por sucursal.'}
          </Typography>
          <Box sx={{ mb: 3 }}>
            {availableLocations.map((location) => (
              <FormControlLabel
                key={location.id}
                control={
                  <Checkbox
                    checked={ecommerceLocationIds.includes(Number(location.id))}
                    onChange={() => handleLocationToggle(location.id)}
                    disabled={loadingLocations || saving}
                  />
                }
                label={location.name}
                sx={{ display: 'block', mb: 1 }}
              />
            ))}
          </Box>
          {ecommerceLocationIds.length > 0 && isFieldConfigurable(defaultLocationPath) && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="default-location-label">
                {t('ecommerce_default_location') || 'Sucursal por defecto'}
              </InputLabel>
              <Select
                labelId="default-location-label"
                value={ecommerceDefaultLocationId ?? ecommerceLocationIds[0] ?? ''}
                label={t('ecommerce_default_location') || 'Sucursal por defecto'}
                onChange={(e) => setEcommerceDefaultLocationId(e.target.value ? Number(e.target.value) : null)}
                disabled={loadingLocations || saving}
              >
                {ecommerceLocationIds.map((id) => {
                  const loc = locations.find((l) => Number(l.id) === id);
                  return loc ? <MenuItem key={id} value={id}>{loc.name}</MenuItem> : null;
                })}
              </Select>
            </FormControl>
          )}
        </Paper>
      )}

      {isAnyPaymentFieldConfigurable && (
        <Paper sx={{ padding: 3, mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t('payment_methods') || 'Métodos de Pago'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('payment_methods_description') || 'Selecciona los métodos de pago que estarán disponibles en el checkout. Debe haber al menos uno habilitado.'}
          </Typography>

          {isFieldConfigurable(paymentMethodsEnabledPath) && (
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={enabledPaymentMethods.includes('mercadopago')}
                    onChange={() => handlePaymentMethodToggle('mercadopago')}
                    disabled={saving}
                  />
                }
                label={t('payment_method_mercadopago') || 'Mercado Pago'}
                sx={{ display: 'block', mb: 1 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={enabledPaymentMethods.includes('transfer')}
                    onChange={() => handlePaymentMethodToggle('transfer')}
                    disabled={saving}
                  />
                }
                label={t('payment_method_transfer') || 'Transferencia Bancaria'}
                sx={{ display: 'block', mb: 1 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={enabledPaymentMethods.includes('cash')}
                    onChange={() => handlePaymentMethodToggle('cash')}
                    disabled={saving}
                  />
                }
                label={t('payment_method_cash') || 'Efectivo'}
                sx={{ display: 'block', mb: 1 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={enabledPaymentMethods.includes('pos')}
                    onChange={() => handlePaymentMethodToggle('pos')}
                    disabled={saving}
                  />
                }
                label={t('payment_method_pos') || 'POS'}
                sx={{ display: 'block', mb: 1 }}
              />
              {enabledPaymentMethods.length === 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {t('at_least_one_payment_method_required') || 'Debe haber al menos un método de pago habilitado'}
                </Alert>
              )}
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'grid', gap: 2 }}>
            <Typography variant="subtitle1">
              {t('payment_method_mercadopago') || 'Mercado Pago'}
            </Typography>
            {isFieldConfigurable('siteConfig.paymentMethods.mercadopago.title') && (
              <TextField
                fullWidth
                label={t('payment_method_title') || 'Título'}
                value={paymentConfigs.mercadopago.title}
                onChange={(e) => handlePaymentConfigChange('mercadopago', 'title', e.target.value)}
              />
            )}
            {isFieldConfigurable('siteConfig.paymentMethods.mercadopago.description') && (
              <TextField
                fullWidth
                multiline
                rows={2}
                label={t('payment_method_description') || 'Descripción'}
                value={paymentConfigs.mercadopago.description}
                onChange={(e) => handlePaymentConfigChange('mercadopago', 'description', e.target.value)}
              />
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'grid', gap: 2 }}>
            <Typography variant="subtitle1">
              {t('payment_method_transfer') || 'Transferencia Bancaria'}
            </Typography>
            {isFieldConfigurable('siteConfig.paymentMethods.transfer.title') && (
              <TextField fullWidth label={t('payment_method_title') || 'Título'} value={paymentConfigs.transfer.title} onChange={(e) => handlePaymentConfigChange('transfer', 'title', e.target.value)} />
            )}
            {isFieldConfigurable('siteConfig.paymentMethods.transfer.description') && (
              <TextField fullWidth multiline rows={2} label={t('payment_method_description') || 'Descripción'} value={paymentConfigs.transfer.description} onChange={(e) => handlePaymentConfigChange('transfer', 'description', e.target.value)} />
            )}
            {isFieldConfigurable('siteConfig.paymentMethods.transfer.bankName') && (
              <TextField fullWidth label={t('transfer_bank_name') || 'Banco'} value={paymentConfigs.transfer.bankName} onChange={(e) => handlePaymentConfigChange('transfer', 'bankName', e.target.value)} />
            )}
            {isFieldConfigurable('siteConfig.paymentMethods.transfer.accountHolder') && (
              <TextField fullWidth label={t('transfer_account_holder') || 'Titular'} value={paymentConfigs.transfer.accountHolder} onChange={(e) => handlePaymentConfigChange('transfer', 'accountHolder', e.target.value)} />
            )}
            {isFieldConfigurable('siteConfig.paymentMethods.transfer.accountNumber') && (
              <TextField fullWidth label={t('transfer_account_number') || 'Número de cuenta'} value={paymentConfigs.transfer.accountNumber} onChange={(e) => handlePaymentConfigChange('transfer', 'accountNumber', e.target.value)} />
            )}
            {isFieldConfigurable('siteConfig.paymentMethods.transfer.accountType') && (
              <TextField fullWidth label={t('transfer_account_type') || 'Tipo de cuenta'} value={paymentConfigs.transfer.accountType} onChange={(e) => handlePaymentConfigChange('transfer', 'accountType', e.target.value)} />
            )}
            {isFieldConfigurable('siteConfig.paymentMethods.transfer.branch') && (
              <TextField fullWidth label={t('transfer_branch') || 'Sucursal'} value={paymentConfigs.transfer.branch} onChange={(e) => handlePaymentConfigChange('transfer', 'branch', e.target.value)} />
            )}
            {isFieldConfigurable('siteConfig.paymentMethods.transfer.instructions') && (
              <TextField fullWidth multiline rows={3} label={t('payment_method_instructions') || 'Instrucciones'} value={paymentConfigs.transfer.instructions} onChange={(e) => handlePaymentConfigChange('transfer', 'instructions', e.target.value)} />
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'grid', gap: 2 }}>
            <Typography variant="subtitle1">
              {t('payment_method_cash') || 'Efectivo'}
            </Typography>
            {isFieldConfigurable('siteConfig.paymentMethods.cash.title') && (
              <TextField fullWidth label={t('payment_method_title') || 'Título'} value={paymentConfigs.cash.title} onChange={(e) => handlePaymentConfigChange('cash', 'title', e.target.value)} />
            )}
            {isFieldConfigurable('siteConfig.paymentMethods.cash.description') && (
              <TextField fullWidth multiline rows={2} label={t('payment_method_description') || 'Descripción'} value={paymentConfigs.cash.description} onChange={(e) => handlePaymentConfigChange('cash', 'description', e.target.value)} />
            )}
            {isFieldConfigurable('siteConfig.paymentMethods.cash.instructions') && (
              <TextField fullWidth multiline rows={3} label={t('payment_method_instructions') || 'Instrucciones'} value={paymentConfigs.cash.instructions} onChange={(e) => handlePaymentConfigChange('cash', 'instructions', e.target.value)} />
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'grid', gap: 2 }}>
            <Typography variant="subtitle1">
              {t('payment_method_pos') || 'POS'}
            </Typography>
            {isFieldConfigurable('siteConfig.paymentMethods.pos.title') && (
              <TextField fullWidth label={t('payment_method_title') || 'Título'} value={paymentConfigs.pos.title} onChange={(e) => handlePaymentConfigChange('pos', 'title', e.target.value)} />
            )}
            {isFieldConfigurable('siteConfig.paymentMethods.pos.description') && (
              <TextField fullWidth multiline rows={2} label={t('payment_method_description') || 'Descripción'} value={paymentConfigs.pos.description} onChange={(e) => handlePaymentConfigChange('pos', 'description', e.target.value)} />
            )}
            {isFieldConfigurable('siteConfig.paymentMethods.pos.instructions') && (
              <TextField fullWidth multiline rows={3} label={t('payment_method_instructions') || 'Instrucciones'} value={paymentConfigs.pos.instructions} onChange={(e) => handlePaymentConfigChange('pos', 'instructions', e.target.value)} />
            )}
          </Box>
        </Paper>
      )}

      {/* Botón de guardar común */}
      {(isFieldConfigurable(ecommerceModePath) || isFieldConfigurable(locationsPath) || isAnyPaymentFieldConfigurable) && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={
              saving || 
              (isFieldConfigurable(locationsPath) && ecommerceLocationIds.length === 0) ||
              enabledPaymentMethods.length === 0
            }
          >
            {saving ? (t('saving') || 'Guardando...') : (t('save') || 'Guardar')}
          </Button>
        </Box>
      )}
    </>
  );
};

export default EcommerceTab;
